// Body of the California loadup (mattress pickup request) form: the order-support
// layout -- the SB 254 program copy, the pickup-request rules list, and the
// LoadUpOfferSubmitRequestForm rendered as a native React field tree (its
// pickup-quantity ChoiceFields are added dynamically per offer, so fields
// arrive serialized per-instance). The field loop stays hand-written rather
// than using the shared FormFields: this page renders no placeholders and no
// data-name hooks.
import { Immutable } from "immer";
import React from "react";
import { FormErrorList } from "tsi-common-react/src/forms/FormErrorList/index";
import { FieldControl } from "tsi-common-react/src/forms/FormPageFields/index";
import {
    FORM_CLASS_NAME,
    FORM_FIELD_CLASS_NAME,
    FORM_FIELD_REQUIRED_CLASS_NAME,
    FORM_FIELD_SUBMIT_CLASS_NAME,
    FORM_LABEL_CLASS_NAME,
} from "tsi-common-react/src/forms/classNames";
import { t } from "ttag";

import { templates } from "@reactivated";

import { CSRFToken } from "@reactivated/forms";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

type IProps = Immutable<templates.LoadupRequestFormDocumentTemplate>;

export const LoadupRequestFormBody = (props: IProps): React.JSX.Element => {
    return (
        <section
            id="loadup-request-form"
            className="loadup-request order-support order-support--content-centered"
        >
            <div className="main-container">
                <div className="detail-container">
                    <h1>{t`Mattress Pickup Request`}</h1>
                    <div className="loadup-request__body">
                        <p>{t`Thank you for your recent purchase.`}</p>
                        <p>
                            {t`California's Used Mattress Recovery and Recycling Act (SB 254), amended with AB 187 requires all retailers that deliver a new mattress to a consumer in California to offer complimentary pick-up of their used mattress, if applicable.`}
                        </p>
                        <p>
                            {t`If you'd like to take advantage of this offer, please complete the form below and you will be contacted within 1-3 business days to schedule an appointment.`}
                        </p>
                        <ul>
                            <li>
                                {t`Pickup quantity can not exceed mattress order quantity.`}
                            </li>
                            <li>
                                {t`One mattress and flat foundation pick up request per form.`}
                            </li>
                            <li>
                                {t`Your shipping address will be used for pick up.`}
                            </li>
                            <li>
                                {t`Mattress disposal is a separate service from delivery.`}
                            </li>
                            <li>
                                {t`Pickup will be performed by a third-party vendor.`}
                            </li>
                        </ul>

                        <form className={FORM_CLASS_NAME} method="post">
                            <CSRFToken />
                            {props.non_field_errors.length > 0 && (
                                <div className={FORM_FIELD_CLASS_NAME}>
                                    <FormErrorList
                                        errors={props.non_field_errors}
                                    />
                                </div>
                            )}
                            {props.fields.map((field) => {
                                if (field.kind === "hidden") {
                                    return (
                                        <FieldControl
                                            key={field.field_id}
                                            field={field}
                                        />
                                    );
                                }
                                const errors =
                                    props.field_errors[field.name] ?? [];
                                const label =
                                    props.field_labels[field.name] ??
                                    field.name;
                                return (
                                    <div
                                        className={concatClassNames([
                                            FORM_FIELD_CLASS_NAME,
                                            field.required
                                                ? FORM_FIELD_REQUIRED_CLASS_NAME
                                                : null,
                                        ])}
                                        key={field.field_id}
                                    >
                                        <label
                                            className={FORM_LABEL_CLASS_NAME}
                                            htmlFor={field.field_id}
                                        >
                                            {label}
                                        </label>
                                        <FieldControl field={field} />
                                        {errors.length > 0 && (
                                            <FormErrorList errors={errors} />
                                        )}
                                    </div>
                                );
                            })}
                            <div className={FORM_FIELD_SUBMIT_CLASS_NAME}>
                                <input
                                    type="submit"
                                    className="button button--full-width"
                                    value={t`Submit`}
                                />
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </section>
    );
};
